document.addEventListener('alpine:init', () => {
  Alpine.data('handleMinicart', () => ({
    init() {
      console.log('handleMinicart init')
    },

    cart: {
      note: null,
      customCheckout: false,
      attributes: {},
      items: [],
      currency: window.Shopify.currency.active,
      cart_level_discount_applications: [],
      response: {
        result: {},
        show: false,
        timeout: 5000,
      },
      item_count: 0,
      total_price: 0,
      total_weight: 0,
      total_discount: 0,
      original_total_price: 0,
      items_subtotal_price: 0
    },

    shippingCosts: window.shippingCosts,

    _abortController: null,
    showLoader: false,

    initAbortController() {
      if (this._abortController) {
        this._abortController.abort('abort previous request');
      }
      this._abortController = new AbortController()
    },

    getAbortControllerSignal() {
      return this._abortController.signal
    },

    resetAbortController() {
      this._abortController = null;
    },

    toggleMiniCart() {
      console.log('(minicart.js) toggleMiniCart called');

      LiquifyHelper.handleTriggerClick();

      this.getCart();
    },

    /* Overridable so a test webhook can be pointed at without touching the asset. */
    checkoutUrl() {
      return 'https://sauer-checkout.gardinen-sauer.workers.dev/';
    },

    /* Attached items keyed by the main product they belong to. */
    componentsByMainProduct(items = this.cart.items) {
      const groups = new Map();

      items.forEach(item => {
        const mainProductId = item.properties?._mainProductId;
        if (!mainProductId) return;

        if (!groups.has(mainProductId)) groups.set(mainProductId, []);
        groups.get(mainProductId).push(item);
      });

      return groups;
    },

    /* How many of a component belong to one unit of the main product. */
    componentsPerUnit(item) {
      const perUnit = parseFloat(item.properties?._baseQuantity);
      return Number.isFinite(perUnit) && perUnit > 0 ? perUnit : 1;
    },

    /* Readable properties describing the components of a collapsed position.

       A name set by the configurator wins: _displayLabel and _displayValue are written
       onto the component line when the variant is selected, which is the only moment
       the intended naming is known. Without them the real product and variant names
       from cart.js are used, so a component always ends up named somehow.

       Keys must not start with an underscore - Shopify hides those from the customer.

       Surcharge components (_isSurcharge) are priced in but never named here - they
       are a price adjustment, not a component the customer picked, and should stay
       invisible in the frontend just like they already are in the order emails. */
    componentProperties(components) {
      const properties = {};

      components.forEach(item => {
        if (item.properties?._isSurcharge) return;

        const label = this.htmlspecialchars_decode(
          item.properties?._displayLabel || item.product_title || item.title || ''
        ).trim();
        if (!label) return;

        const rawVariant = item.properties?._displayValue
          || (item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '');
        const variantTitle = this.htmlspecialchars_decode(rawVariant).trim();

        const perUnit = this.componentsPerUnit(item);
        const parts = [];
        if (variantTitle) parts.push(variantTitle);
        if (perUnit > 1) parts.push(`${perUnit}x`);

        // Two components can share a product title, so never overwrite a key
        let key = label;
        let suffix = 2;
        while (properties[key] !== undefined) key = `${label} ${suffix++}`;

        properties[key] = parts.length ? parts.join(', ') : String(perUnit);
      });

      return properties;
    },

    /* One entry per position that should show up in the checkout. A configured
       product collapses into a single entry that carries the merged unit price and
       lists its components in the properties. Everything else stays a plain
       variant line, so standalone products are unaffected. */
    buildCheckoutItems() {
      const groups = this.componentsByMainProduct();
      const items = [];

      this.cart.items.forEach(item => {
        // Attached items are folded into their main product instead of being sent
        if (item.properties?._mainProductId) return;

        const components = groups.get(item.properties?._id) ?? [];
        const basePrice = parseFloat(item.properties?._totalBasePrice);
        const hasBasePrice = Number.isFinite(basePrice);

        /* A plain product has no configuration, so its own variant price is the price
           of the position. It is sent explicitly: leaving the field out means the
           webhook has to handle a missing value, and an absent price mapped through
           arithmetic silently becomes 0 - which is how standalone items ended up free
           in the checkout as soon as a configured product shared the cart with them.
           `consolidated` stays the flag that says whether components were merged in. */
        if (!components.length && !hasBasePrice) {
          items.push({
            id: item.id,
            quantity: item.quantity,
            unitPrice: item.price,
            consolidated: false,
            properties: item.properties
          });
          return;
        }

        /* _totalBasePrice already covers the main product plus every component for
           one unit. Without it, sum the parts so a position can never come out
           cheaper than what it contains. */
        const componentSum = components.reduce(
          (sum, component) => sum + component.price * this.componentsPerUnit(component),
          0
        );

        items.push({
          id: item.id,
          quantity: item.quantity,
          unitPrice: hasBasePrice ? Math.round(basePrice) : item.price + componentSum,
          consolidated: true,
          properties: {
            ...item.properties,
            ...this.componentProperties(components)
          },
          // Optional for the webhook: the raw components behind the collapsed line
          components: components.map(component => ({
            id: component.id,
            quantity: component.quantity,
            perUnit: this.componentsPerUnit(component),
            title: this.htmlspecialchars_decode(component.title || '')
          }))
        });
      });

      return items;
    },

    async goToCustomCheckout() {
      this.showLoader = true

      try {
        const response = await fetch(this.checkoutUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: this.buildCheckoutItems() })
        });

        if (!response.ok) throw new Error('Cart response not ok');

        const result = (await response.text()).trim();

        /* Only navigate to something that really is an absolute http(s) URL. An error
           text from the webhook would otherwise be treated as a relative path and
           send the customer into a broken page. */
        let target;
        try {
          target = new URL(result);
          if (target.protocol !== 'https:' && target.protocol !== 'http:') throw new Error('protocol');
        } catch {
          throw new Error(`Checkout response is not a URL: ${result.slice(0, 200)}`);
        }

        /* The draft order owns the items from here on. Clearing the Shopify
           cart keeps the customer from wandering into the normal checkout with
           lines whose prices were never consolidated. The redirect matters more
           than the cleanup, so a failed clear is only logged. */
        try {
          await fetch(window.Shopify.routes.root + 'cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });
        } catch (clearError) {
          console.warn('Could not clear cart after checkout handover:', clearError);
        }

        document.dispatchEvent(new Event('cartupdated', { bubbles: true }))
        window.location.href = target.href;

        return result;
      } catch (error) {
        console.error("Cart error:", error);
        this.$dispatch('showcartmessage', { status: 'error', message: error.message, description: error.message });
      } finally {
        this.showLoader = false
      }
    },

    /**
     * Get the cart data.
     */
    async getCart() {
      this.cart.customCheckout = false;
      this.initAbortController()
      await fetch(window.Shopify.routes.root + 'cart.js', {
        method: 'GET',
        signal: this.getAbortControllerSignal(),
        headers: { 'Content-Type': 'application/json' },
      })
        .then(response => response.json())
        .then(data => {
          this.resetAbortController();

          /* The cart hides attached items from the list, so the main line has to carry
             the readable component list itself. Derived here from the real cart lines
             instead of from what the configurator stored, which never covered
             suspension, holder and surcharges. */
          const componentGroups = this.componentsByMainProduct(data.items);

          this.cart.items = data.items.map((item) => {
            item.title = this.htmlspecialchars_decode(item.title);
            const mainProductId = item.properties?._mainProductId

            const components = componentGroups.get(item.properties?._id) ?? [];
            if (!mainProductId && components.length) {
              item.properties = { ...item.properties, ...this.componentProperties(components) };
            }

            /* Use the already calculated totalBasePrice to extrapolate the totalLinePrice */
            if (item.properties && item.properties._totalBasePrice) {
              const totalBasePrice = parseFloat(item.properties._totalBasePrice);

              if (!isNaN(totalBasePrice)) {
                item.total_base_price = totalBasePrice;
                item.total_line_price = totalBasePrice * item.quantity;
              }
            } else {
              item.total_base_price = item.price;
              item.total_line_price = item.line_price;
            }

            /* Sync with mainProduct quantity */
            if (mainProductId) {
              const mainProduct = data.items.find(dataItem => dataItem.properties?._id === mainProductId)
              const baseQuantity = item.properties?._baseQuantity

              if (mainProduct && baseQuantity && mainProduct.quantity * baseQuantity !== item.quantity) {
                const finalQuantity = baseQuantity ? baseQuantity * mainProduct.quantity : mainProduct.quantity
                this.updateCartItemQuantity(item.key, finalQuantity)
              } else if (mainProduct && !baseQuantity && mainProduct.quantity !== item.quantity) {
                this.updateCartItemQuantity(item.key, mainProduct.quantity)
              } else if (!mainProduct) {
                this.updateCartItemQuantity(item.key, 0)
              }

            }

            return item;
          });

          /* Every checkout goes through the draft-order worker now, plain carts
             included — otherwise standalone purchases would still reach the
             normal checkout. Only an empty cart keeps the /checkout fallback
             link, where Shopify just shows the empty cart. */
          this.cart.customCheckout = this.cart.items.length > 0;

          this.cart.item_count = this.cart.items.reduce((sum, item) => {
            if (item.properties?._mainProductId) {
              return sum;
            }
            return sum + item.quantity;
          }, 0);

          this.cart.total_price = this.cart.items.reduce((sum, item) => {
            if (item.properties?._mainProductId) {
              return sum;
            }
            return sum + (item.total_line_price || item.line_price);
          }, 0);

          this.cart.total_weight = data.total_weight;
          this.cart.total_discount = data.total_discount;
          this.cart.original_total_price = data.original_total_price;

          this.$dispatch('carttotalitems', this.cart.item_count);
          this.$dispatch('currentcartitems', this.cart.items);
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    },

    /**
     * @param key
     * @param quantity
     */
    increaseCartItemQuantity(key, quantity) {
      this.updateCartItemQuantity(key, parseInt(quantity) + 1);
    },

    /**
     * @param key
     * @param quantity
     */
    decreaseCartItemQuantity(key, quantity) {
      this.updateCartItemQuantity(key, parseInt(quantity) - 1);
    },

    /**
     * Update the cart item.
     *
     * @param key
     * @param quantity
     */
    updateCartItemQuantity(key, quantity) {
      this.initAbortController();
      console.log('updateCartItemQuantity(): key, quantity: ', key, quantity);
      this.cart.items.filter((product) => {
        if (product.key === key) {
          product.quantity = quantity
        }
      })
      let updates = {};
      updates[key] = quantity;
      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        signal: this.getAbortControllerSignal(),
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      })
        .then(response => response.json())
        .then(data => {
          this.resetAbortController();
          console.log('updateCartItemQuantity(): ', data);

          this.$dispatch('cartupdated', this.cart.items);
          this.$dispatch('showcartmessage', { status: data.status, message: data.message, description: data.description });
        })
        .catch((error) => {
          console.error('Error:', error);
          this.$dispatch('showcartmessage', { status: error?.status, message: error, description: error });
        });
    },

    /**
     * Format monetary values.
     */
    moneyFormat(value, minor = true) {
      return LiquifyHelper.moneyFormat(value, minor)
    },

    htmlspecialchars_decode(string) {
      return LiquifyHelper.htmlspecialchars_decode(string)
    },

    /**
     * Shows the minicart api message
     * @param event
     */
    showCartMessage(event) {
      if (event?.detail?.status) {
        this.cart.response.result = event.detail ?? {}
        this.cart.response.show = true
        setTimeout(() => {
          this.cart.response.result = {};
          this.cart.response.show = false
        }, this.cart.response.timeout ?? 5000)
      }
    },

    async returnCartItems() {
      await this.getCart();

      this.$dispatch('currentcartitems', this.cart.items);
    },

    /**
     * Ermittelt die aktuell gültige Versandkosten-Stufe anhand des
     * Warenkorbwerts (cart.total_price in Cent).
     * @returns {{threshold: number, cost: number}}
     */
    get currentShippingTier() {
      const total = this.cart.total_price;
      const tiers = [...this.shippingCosts].sort((a, b) => a.threshold - b.threshold);

      return tiers.reduce(
        (match, tier) => (total >= tier.threshold ? tier : match),
        tiers[0] ?? { threshold: 0, cost: 0 }
      );
    },

    /**
     * Aktuelle Versandkosten in Cent.
     */
    get shippingCost() {
      return this.currentShippingTier.cost;
    },

    /**
     * True, wenn der Versand aktuell kostenlos ist.
     */
    get hasFreeShipping() {
      return this.shippingCost === 0;
    },

    /**
     * Die nächsthöhere Stufe, die günstigeren (oder kostenlosen) Versand
     * bietet – oder null, wenn bereits die beste Stufe erreicht ist.
     * @returns {{threshold: number, cost: number}|null}
     */
    get nextShippingTier() {
      const total = this.cart.total_price;
      const current = this.currentShippingTier;

      return [...this.shippingCosts]
        .sort((a, b) => a.threshold - b.threshold)
        .find(tier => tier.threshold > total && tier.cost < current.cost) ?? null;
    },

    /**
     * Fehlbetrag in Cent bis zur nächsten Versandvorteil-Stufe.
     * 0, wenn keine bessere Stufe mehr existiert.
     */
    get remainingForNextTier() {
      const next = this.nextShippingTier;
      if (!next) return 0;

      return Math.max(0, next.threshold - this.cart.total_price);
    },

    /**
     * Fortschritt (0–1) bis zur nächsten Versandvorteil-Stufe.
     * Praktisch für Progress-Bars ("Nur noch X € bis zum Gratis-Versand").
     */
    get shippingProgress() {
      const next = this.nextShippingTier;
      if (!next || next.threshold <= 0) return 1;

      return Math.min(1, this.cart.total_price / next.threshold);
    },

    /**
     * @deprecated
     */
    set open(open) { // deprecated use  LiquifyHelper instead
      //console.warn('Deprecated "set open" in mini_cart')
    },
    /**
     * @deprecated
     */
    get open() { // deprecated use  LiquifyHelper instead
      //console.warn('Deprecated "get open" in mini_cart')
      return false;
    },
    /**
     * @deprecated
     */
    set note(note) { // deprecated
      //console.warn('Deprecated "set note" in mini_cart')
      this.cart.note = note;
    },
    /**
     * @deprecated
     */
    get note() { // deprecated
      //console.warn('Deprecated "get note" in mini_cart')
      return this.cart.note;
    },
    /**
     * @deprecated
     */
    set attributes(attributes) { // deprecated
      //console.warn('Deprecated "set attributes" in mini_cart')
      this.cart.attributes = attributes;
    },
    /**
     * @deprecated
     */
    get attributes() { // deprecated
      //console.warn('Deprecated "get attributes" in mini_cart')
      return this.cart.attributes;
    },
    /**
     * @deprecated
     */
    set original_total_price(original_total_price) { //deprecated
      //console.warn('Deprecated "set original_total_price" in mini_cart')
      this.cart.original_total_price = original_total_price;
    },
    /**
     * @deprecated
     */
    get original_total_price() { //deprecated
      //console.warn('Deprecated "get original_total_price" in mini_cart')
      return this.cart.original_total_price;
    },
    /**
     * @deprecated
     */
    set total_price(total_price) { //deprecated
      //console.warn('Deprecated "set total_price" in mini_cart')
      this.cart.total_price = total_price;
    },
    /**
     * @deprecated
     */
    get total_price() { //deprecated
      //console.warn('Deprecated "get total_price" in mini_cart')
      return this.cart.total_price;
    },
    /**
     * @deprecated
     */
    set total_discount(total_discount) { //deprecated
      //console.warn('Deprecated "set total_discount" in mini_cart')
      this.cart.total_discount = total_discount;
    },
    /**
     * @deprecated
     */
    get total_discount() { //deprecated
      //console.warn('Deprecated "get total_discount" in mini_cart')
      return this.cart.total_discount;
    },
    /**
     * @deprecated
     */
    set total_weight(total_weight) { //deprecated
      //console.warn('Deprecated "set total_weight" in mini_cart')
      this.cart.total_discount = total_weight;
    },
    /**
     * @deprecated
     */
    get total_weight() { //deprecated
      //console.warn('Deprecated "get total_weight" in mini_cart')
      return this.cart.total_weight;
    },
    /**
     * @deprecated
     */
    set item_count(item_count) { //deprecated
      //console.warn('Deprecated "set item_count" in mini_cart')
      this.cart.total_discount = item_count;
    },
    /**
     * @deprecated
     */
    get item_count() { //deprecated
      //console.warn('Deprecated "get item_count" in mini_cart')
      return this.cart.item_count;
    },
    /**
     * @deprecated
     */
    set items_subtotal_price(items_subtotal_price) { //deprecated
      //console.warn('Deprecated "set items_subtotal_price" in mini_cart')
      this.cart.items_subtotal_price = items_subtotal_price;
    },
    /**
     * @deprecated
     */
    get items_subtotal_price() { //deprecated
      //console.warn('Deprecated "get items_subtotal_price" in mini_cart')
      return this.cart.items_subtotal_price;
    },
    /**
     * @deprecated
     */
    set products(products) {
      //console.warn('Deprecated "set products" in mini_cart')
      this.cart.items = products;
    },
    /**
     * @deprecated
     */
    get products() {
      //console.warn('Deprecated "get products" in mini_cart')
      return this.cart.items;
    },
    /**
     * @deprecated
     */
    set requires_shipping(requires_shipping) {
      //console.warn('Deprecated "set requires_shipping" in mini_cart')
    },
    /**
     * @deprecated
     */
    get requires_shipping() {
      //console.warn('Deprecated "get requires_shipping" in mini_cart')
      return false;
    },
    /**
     * @deprecated
     */
    set currency(currency) { //deprecated
      //console.warn('Deprecated "set currency" in mini_cart')
      this.cart.currency = currency;
    },
    /**
     * @deprecated
     */
    get currency() { //deprecated
      //console.warn('Deprecated "get currency" in mini_cart')
      return this.cart.currency;
    },
    /**
     * @deprecated
     */
    set cart_level_discount_applications(cart_level_discount_applications) { //deprecated
      //console.warn('Deprecated "set cart_level_discount_applications" in mini_cart')
      this.cart.cart_level_discount_applications = cart_level_discount_applications;
    },
    /**
     * @deprecated
     */
    get cart_level_discount_applications() { //deprecated
      //console.warn('Deprecated "get cart_level_discount_applications" in mini_cart')
      return this.cart.cart_level_discount_applications;
    },
    /**
     * @deprecated
     */
    set cartApiResponse(cartApiResponse) { //deprecated
      //console.warn('Deprecated "set cartApiResponse" in mini_cart')
      this.cart.cartApiResponse = cartApiResponse;
    },
    /**
     * @deprecated
     */
    get cartApiResponse() { //deprecated
      //console.warn('Deprecated "get cartApiResponse" in mini_cart')
      return this.cart.response;
    },
    /**
     * @deprecated
     */
    set total(total) { //deprecated
      //console.warn('Deprecated "set total" in mini_cart')
      this.cart.item_count = total.items;
      this.cart.total_price = total.price;
      this.cart.total_weight = total.weight;
      this.cart.total_discount = total.discount;
    },
    /**
     * @deprecated
     */
    get total() { //deprecated
      //console.warn('Deprecated "get total" in mini_cart')
      return {
        items: this.cart.item_count,
        price: this.cart.total_price,
        weight: this.cart.total_weight,
        discount: this.cart.total_discount,
      };
    }
  }))


});
